import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge } from 'react-bootstrap';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './teStyle.css';
import config from '../../config.json';

const apiUrl = config.apiUrl;

const getStatusBadge = (status) => {
    let variant;
    switch (status) {
        case 'Pending':
            variant = 'primary';
            break;
        case 'Approved':
            variant = 'success';
            break;
        case 'Rejected':
            variant = 'danger';
            break;
        default:
            variant = 'secondary';
    }
    return <Badge bg={variant}>{status}</Badge>;
};

const Statuses = {
    Pending: 0,
    Approved: 1,
    Rejected: 2
};

const TimeEntryList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [currentEmployeeId, setCurrentEmployeeId] = useState(null);
    const [timeEntryData, setTimeEntryData] = useState([]);
    const [selectedTimeEntryIds, setSelectedTimeEntryIds] = useState([]);
    const [dropdownStates, setDropdownStates] = useState({});
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearchInput, setDebouncedSearchInput] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pendingAction, setPendingAction] = useState('');
    const navigate = useNavigate();
    const selectAllRef = useRef(null);
    const [statusFilters, setStatusFilters] = useState({
        Pending: true,
        Approved: true,
        Rejected: true,
    });

    const roles = JSON.parse(localStorage.getItem('Roles'));

    const userAccess = {
        hasView: roles?.includes('View') || false,
        hasAdmin: roles?.includes('Admin') || false,
        hasApprover: roles?.includes('Approver') || false,
        hasStandard: roles?.includes('Standard') || false,
    };

    const { hasView, hasAdmin, hasStandard, hasApprover } = userAccess;

    const toggleDropdown = (itemId) => {
        setDropdownStates((prevState) => ({
            ...prevState,
            [itemId]: !prevState[itemId],
        }));
    };

    const handleCheckboxChange = (event) => {
        const { name, checked } = event.target;
        setStatusFilters({
            ...statusFilters,
            [name]: checked,
        });
    };

    const fetchEmployeeNameList = async () => {
        const token = localStorage.getItem('token');

        try {
            const response = await fetch(`${apiUrl}/Employee/Namelist`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const result = await response.json();
                const employee = result.Employee && result.Employee.length > 0 ? result.Employee[0] : null;
                setCurrentEmployeeId(employee ? employee.EmployeeID : null);
            }
        } catch (error) {
            setCurrentEmployeeId(null);
        }
    };

    const handleTimeEntrySelection = (id, checked) => {
        setSelectedTimeEntryIds((prevIds) => {
            if (checked) {
                return prevIds.includes(id) ? prevIds : [...prevIds, id];
            }

            return prevIds.filter((selectedId) => selectedId !== id);
        });
    };

    const fetchTimeEntryData = async (searchTerm) => {
        const token = localStorage.getItem('token');
        const statusQueryString = Object.keys(statusFilters)
            .filter((key) => statusFilters[key])
            .map((key) => `statuses=${Statuses[key]}`)
            .join('&');

        const employeeQueryString = currentEmployeeId && !userAccess.hasApprover ? `&employeeID=${currentEmployeeId}` : '';
        let apiUrlEndpoint = `${apiUrl}/TimeEntry?searchTerm=${searchTerm}&${statusQueryString}${employeeQueryString}&pageNumber=${currentPage}&pageSize=${itemsPerPage}`;
        if (userAccess.hasApprover) {
            apiUrlEndpoint = `${apiUrl}/TimeEntry/ForApproval?searchTerm=${searchTerm}&pageNumber=${currentPage}&pageSize=${itemsPerPage}`;
        }
        try {
            const response = await fetch(apiUrlEndpoint, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const result = await response.json();
                setTimeEntryData(result.Data || []);
                setTotalPages(result.TotalPages || 0);
                setSelectedTimeEntryIds([]);
            } else {
                setErrorMessage('Error fetching data');
                setSuccessMessage('');
            }
        } catch (error) {
            setErrorMessage('Error fetching data');
            setSuccessMessage('');
        }
        setIsLoading(false);
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearchInput(searchInput);
        }, 1);

        return () => clearTimeout(timeoutId);
    }, [searchInput]);

    useEffect(() => {
        if (!userAccess.hasApprover) {
            fetchEmployeeNameList();
        }
    }, []);

    useEffect(() => {
        if (!userAccess.hasApprover && !currentEmployeeId) {
            return;
        }
        fetchTimeEntryData(debouncedSearchInput);
    }, [debouncedSearchInput, statusFilters, currentPage, itemsPerPage, currentEmployeeId]);

    const currentItems = timeEntryData;
    const selectedCurrentItemsCount = currentItems.filter((item) => selectedTimeEntryIds.includes(item.ID)).length;
    const areAllCurrentItemsSelected = currentItems.length > 0 && selectedCurrentItemsCount === currentItems.length;
    const hasMeaningfulBulkSelection = selectedCurrentItemsCount >= 2;

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = hasMeaningfulBulkSelection && !areAllCurrentItemsSelected;
        }
    }, [areAllCurrentItemsSelected, hasMeaningfulBulkSelection]);

    const updateTimeEntryStatus = async (ids, status) => {
        const token = localStorage.getItem('token');
        const actionLabel = status === 'Approved' ? 'approval' : 'rejection';
        const endpointAction = status === 'Approved' ? 'Approve' : 'Reject';
        setIsSubmitting(true);
        setPendingAction(status);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const responses = await Promise.all(
                ids.map(async (id) => {
                    const timeEntryRecord = timeEntryData.find((item) => item.ID === id);
                    const response = await fetch(`${apiUrl}/TimeEntry/${endpointAction}/${id}`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            Remarks: timeEntryRecord?.Remarks ?? '',
                        }),
                    });

                    let data = null;
                    try {
                        data = await response.json();
                    } catch (parseError) {
                        data = null;
                    }

                    return { response, data };
                })
            );

            const failedResponse = responses.find(({ response }) => !response.ok);
            if (failedResponse) {
                setErrorMessage(failedResponse.data?.Message || `Unable to complete the ${actionLabel} request.`);
                return;
            }

            setSuccessMessage(status === 'Approved' ? 'Successfully Approved' : 'Successfully Rejected');
            setSelectedTimeEntryIds([]);
            setTimeout(async () => {
                setSuccessMessage('');
                await fetchTimeEntryData(debouncedSearchInput);
            }, 3000);
        } catch (error) {
            setErrorMessage(`Unable to complete the ${actionLabel} request.`);
        } finally {
            setIsSubmitting(false);
            setPendingAction('');
        }
    };

    const handleTimeEntryApproval = async (status) => {
        if (selectedTimeEntryIds.length === 0) {
            return;
        }

        await updateTimeEntryStatus(selectedTimeEntryIds, status);
    };

    const handleSelectAllTimeEntries = () => {
        const currentItemIds = currentItems.map((item) => item.ID);
        setSelectedTimeEntryIds((prevIds) => {
            const hasAnySelectedCurrentItems = currentItemIds.some((id) => prevIds.includes(id));

            if (hasAnySelectedCurrentItems) {
                return prevIds.filter((id) => !currentItemIds.includes(id));
            }

            const nextIds = [...prevIds];
            currentItemIds.forEach((id) => {
                if (!nextIds.includes(id)) {
                    nextIds.push(id);
                }
            });
            return nextIds;
        });
    };

    return (
        <div>
            {(userAccess.hasView) ? (
                <>
                    <h3>Time Entries</h3>
                    {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                    {successMessage && <div className="alert alert-success">{successMessage}</div>}
                    {(userAccess.hasStandard || userAccess.hasAdmin) && (
                        <Link to='/timeentry-create' className='btn btn-primary float-end mb-2'>Add time entry</Link>
                    )}

                    <div className='d-flex mb-2'>
                        <div className='custom-width'>
                            <input
                                type='text'
                                placeholder='Search..'
                                className='form-control'
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                        </div>

                        {hasApprover && selectedTimeEntryIds.length > 0 && (
                            <>
                                <button
                                    type='button'
                                    className='btn btn-success ms-2'
                                    disabled={selectedTimeEntryIds.length === 0 || isSubmitting}
                                    onClick={() => handleTimeEntryApproval('Approved')}
                                >
                                    {isSubmitting && pendingAction === 'Approved' ? 'Approving...' : 'Approve'}
                                </button>
                                <button
                                    type='button'
                                    className='btn btn-danger ms-2'
                                    disabled={selectedTimeEntryIds.length === 0 || isSubmitting}
                                    onClick={() => handleTimeEntryApproval('Rejected')}
                                >
                                    {isSubmitting && pendingAction === 'Rejected' ? 'Rejecting...' : 'Reject'}
                                </button>
                            </>
                        )}
                    </div>

                    <table className='custom-table'>
                        <thead>
                            <tr>
                                <th className='border-end-0'></th>
                                {hasApprover && (
                                    <th className='border-start-0 border-end-0 text-center'>
                                        <input
                                            ref={selectAllRef}
                                            type='checkbox'
                                            className='form-check-input'
                                            checked={areAllCurrentItemsSelected}
                                            onChange={handleSelectAllTimeEntries}
                                        />
                                    </th>
                                )}
                                <th className='border-start-0 border-end-0'>Transaction Date</th>
                                <th className='border-start-0 border-end-0'>Employee</th>
                                <th className='border-start-0 border-end-0'>Start Date</th>
                                <th className='border-start-0 border-end-0'>End Date</th>
                                <th className='border-start-0 border-end-0'>Duration</th>
                                <th className='border-start-0 border-end-0'>Entry Type</th>
                                <th className='border-start-0'>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={hasApprover ? 9 : 8}>
                                        <div className="d-flex align-items-center">
                                            <div className="spinner-border me-2" aria-hidden="true"></div>
                                            <span role="status"> Loading...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                timeEntryData && timeEntryData.length > 0 ? (
                                    timeEntryData.map((item) => (
                                        <React.Fragment key={item.ID}>
                                            <tr>
                                                <td className="text-sm-center remove-space border-end-0" style={{ borderBottom: 'none' }}>
                                                    <div className="dropdown">
                                                        <button
                                                            className="btn"
                                                            type="button"
                                                            id={`dropdownMenuButton${item.ID}`}
                                                            data-bs-toggle="dropdown"
                                                            data-bs-auto-close="outside"
                                                            aria-haspopup="true"
                                                            aria-expanded={dropdownStates[item.ID]}
                                                            onClick={() => toggleDropdown(item.ID)}
                                                        >
                                                            <i className="fas fa-ellipsis"></i>
                                                        </button>
                                                        <div
                                                            className={`dropdown-menu ${dropdownStates[item.ID] ? 'show' : ''}`}
                                                            aria-labelledby={`dropdownMenuButton${item.ID}`}
                                                        >
                                                            {(userAccess.hasStandard || userAccess.hasAdmin) && (
                                                                <Link to={`/timeentry-update/${item.ID}`}
                                                                    className={`dropdown-item ${item.Status === 'Approved' || item.Status === 'Rejected' ? 'disabled' : ''}`}>
                                                                    Update
                                                                </Link>
                                                            )}
                                                            {(userAccess.hasView || userAccess.hasAdmin) && (
                                                                <Link to={`/timeentry-view/${item.ID}`} className="dropdown-item">
                                                                    View
                                                                </Link>
                                                            )}
                                                            {(userAccess.hasStandard || userAccess.hasAdmin) && (
                                                                <Link to={`/timeentry-delete/${item.ID}`}
                                                                    className={`dropdown-item ${item.Status === 'Approved' || item.Status === 'Rejected' ? 'disabled' : ''}`}>
                                                                    Delete
                                                                </Link>
                                                            )}
                                                            {(userAccess.hasApprover || userAccess.hasAdmin) && (
                                                                <Link to={`/timeentry-approve/${item.ID}`}
                                                                    className={`dropdown-item ${item.Status === 'Approved' || item.Status === 'Rejected' ? 'disabled' : ''}`}>
                                                                    Approve
                                                                </Link>
                                                            )}
                                                            {(userAccess.hasApprover || userAccess.hasAdmin) && (
                                                                <Link to={`/timeentry-reject/${item.ID}`}
                                                                    className={`dropdown-item ${item.Status === 'Approved' || item.Status === 'Rejected' ? 'disabled' : ''}`}>
                                                                    Reject
                                                                </Link>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                {hasApprover && (
                                                    <td className='border-start-0 border-end-0 text-center align-middle' style={{ borderBottom: 'none' }}>
                                                        <input
                                                            type='checkbox'
                                                            className='form-check-input'
                                                            checked={selectedTimeEntryIds.includes(item.ID)}
                                                            onChange={(event) => handleTimeEntrySelection(item.ID, event.target.checked)}
                                                        />
                                                    </td>
                                                )}
                                                <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.TransactionDate}</td>
                                                <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.Employee.Name}</td>
                                                <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.StartDate}</td>
                                                <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.EndDate}</td>
                                                <td className='text-end border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.Duration}</td>
                                                <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.EntryTypeID}</td>
                                                <td className='border-start-0' style={{ borderBottom: 'none' }}>{getStatusBadge(item.Status)}</td>
                                            </tr>
                                            <tr>
                                                <td colSpan={hasApprover ? 9 : 8} style={{ borderTop: 'none', paddingLeft: hasApprover ? '105px' : '65px', maxWidth: '50px', whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                                                    <strong>Remarks:</strong> {item.Remarks}
                                                </td>
                                            </tr>
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={hasApprover ? 9 : 8}>No Record</td>
                                    </tr>
                                )

                            )}
                        </tbody>
                    </table>
                    <nav aria-label="Page navigation">
                        <ul className="pagination mt-3">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(1)}
                                    aria-label="First"
                                >
                                    <i className="fas fa-angle-double-left" aria-hidden="true"></i>
                                </button>
                            </li>
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} aria-label="Previous">
                                    <i className="fas fa-angle-left" aria-hidden="true"></i>
                                </button>
                            </li>
                            {(() => {
                                const pageGroupSize = 5;
                                const currentGroup = Math.floor((currentPage - 1) / pageGroupSize);
                                const startPage = currentGroup * pageGroupSize + 1;
                                const endPage = Math.min(startPage + pageGroupSize - 1, totalPages);

                                const visiblePages = Array.from(
                                    { length: endPage - startPage + 1 },
                                    (_, index) => startPage + index
                                );

                                return visiblePages.map((page) => (
                                    <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                        <button className="page-link" onClick={() => setCurrentPage(page)}>
                                            {page}
                                        </button>
                                    </li>
                                ));
                            })()}
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} aria-label="Next">
                                    <i className="fas fa-angle-right" aria-hidden="true"></i>
                                </button>
                            </li>
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(totalPages)}
                                    aria-label="Last"
                                >
                                    <i className="fas fa-angle-double-right" aria-hidden="true"></i>
                                </button>
                            </li>
                        </ul>
                    </nav>
                </>
            ) : (
                <div>
                    <h3>No access!</h3>
                    <button className="btn btn-primary" onClick={() => navigate(-1)}>
                        Go Back
                    </button>
                </div>
            )}
        </div>
    )
}

export default TimeEntryList
